/**
 * QR Code Scanner Utility
 * Client-side utility for scanning QR codes from images using jsqr
 */

import jsQR from 'jsqr';

/**
 * Scan QR code from File object
 */
export async function scanQRCodeFromFile(file: File): Promise<string | null> {
  try {
    const imageData = await createImageDataFromFile(file);
    const result = jsQR(imageData.data, imageData.width, imageData.height);

    return result ? result.data : null;
  } catch (error) {
    throw new Error(`Failed to scan QR code: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Scan QR code from image URL
 */
export async function scanQRCodeFromUrl(imageUrl: string): Promise<string | null> {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const blob = await response.blob();
    const imageData = await createImageDataFromBlob(blob);
    const result = jsQR(imageData.data, imageData.width, imageData.height);

    return result ? result.data : null;
  } catch (error) {
    throw new Error(`Failed to scan QR code: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Create ImageData from File object with enhanced processing for mobile images
 */
function createImageDataFromFile(file: File): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    const img = new Image();

    img.onload = () => {
      // Process image for better QR scanning
      const MAX_SIZE = 800;
      let width = img.width;
      let height = img.height;

      // Scale down large images for better performance
      if (width > MAX_SIZE || height > MAX_SIZE) {
        const scale = Math.min(MAX_SIZE / width, MAX_SIZE / height);
        width = Math.floor(width * scale);
        height = Math.floor(height * scale);
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and process image
      ctx.drawImage(img, 0, 0, width, height);

      // Apply image enhancement for QR code detection
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const processedImageData = enhanceImageForQR(imageData);

      ctx.putImageData(processedImageData, 0, 0);
      const finalImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      resolve(finalImageData);
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Create ImageData from Blob object with enhanced processing for mobile images
 */
function createImageDataFromBlob(blob: Blob): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    const img = new Image();

    img.onload = () => {
      // Process image for better QR scanning
      const MAX_SIZE = 800;
      let width = img.width;
      let height = img.height;

      // Scale down large images for better performance
      if (width > MAX_SIZE || height > MAX_SIZE) {
        const scale = Math.min(MAX_SIZE / width, MAX_SIZE / height);
        width = Math.floor(width * scale);
        height = Math.floor(height * scale);
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and process image
      ctx.drawImage(img, 0, 0, width, height);

      // Apply image enhancement for QR code detection
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const processedImageData = enhanceImageForQR(imageData);

      ctx.putImageData(processedImageData, 0, 0);
      const finalImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      resolve(finalImageData);
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(blob);
  });
}

/**
 * Enhance image for better QR code detection
 */
function enhanceImageForQR(imageData: ImageData): ImageData {
  const data = imageData.data;
  const width = imageData.width;
  const height = imageData.height;

  // Convert to grayscale
  for (let i = 0; i < data.length; i += 4) {
    const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    data[i] = gray;     // Red
    data[i + 1] = gray; // Green
    data[i + 2] = gray; // Blue
    // Alpha channel remains unchanged
  }

  // Apply contrast enhancement
  const histogram = new Array(256).fill(0);

  // Calculate histogram
  for (let i = 0; i < data.length; i += 4) {
    histogram[data[i]]++;
  }

  // Calculate cumulative histogram and find min/max
  let min = 0;
  let max = 255;
  let cumulative = 0;
  const totalPixels = width * height;

  for (let i = 0; i < 256; i++) {
    cumulative += histogram[i];
    if (cumulative < totalPixels * 0.05) min = i;
    if (cumulative < totalPixels * 0.95) max = i;
  }

  // Apply contrast stretching
  const scale = max > min ? 255 / (max - min) : 1;
  for (let i = 0; i < data.length; i += 4) {
    let value = (data[i] - min) * scale;
    value = Math.max(0, Math.min(255, value));
    data[i] = value;
    data[i + 1] = value;
    data[i + 2] = value;
  }

  return imageData;
}

/**
 * Check if the environment supports QR code scanning
 */
export function isQRScanningSupported(): boolean {
  return typeof document !== 'undefined' && typeof window !== 'undefined';
}